import React from 'react';
import { fmtFull } from '../../lib/format';

/**
 * Controlled currency input with MAX/quick-percentage shortcuts and a
 * visual balance bar. Extracted out of VaultPanel to keep that component
 * focused on orchestration.
 */
export default function VaultAmountInput({
  mode,
  amount,
  onAmountChange,
  maxAvailable,
}) {
  const parsed = Number(amount) || 0;
  const percentOfMax = maxAvailable > 0 ? Math.min(100, (parsed / maxAvailable) * 100) : 0;

  const handleQuick = (pct) =>
    onAmountChange(((maxAvailable * pct) / 100).toFixed(4));
  const handleMax = () => onAmountChange(String(maxAvailable || 0));

  const inputTestId = mode === 'deposit' ? 'deposit-input' : 'withdraw-input';
  const balanceTestId =
    mode === 'deposit' ? 'pusd-balance-inline' : 'ypusd-balance-inline';
  const unit = mode === 'deposit' ? 'PUSD' : 'yPUSD';
  const tokenGlyph = mode === 'deposit' ? 'P' : 'y';

  return (
    <>
      <label className="block text-[11px] tracking-[0.22em] uppercase text-palm-mint/70">
        {mode === 'deposit' ? 'You deposit' : 'You redeem'}
      </label>
      <div className="mt-3 rounded-2xl bg-black/30 border border-white/5 p-4 focus-within:border-palm-lime/60 transition-colors">
        <div className="flex items-center justify-between gap-4">
          <input
            value={amount}
            onChange={(e) => onAmountChange(e.target.value.replace(/[^0-9.]/g, ''))}
            inputMode="decimal"
            placeholder="0.00"
            className="min-w-0 flex-1 bg-transparent outline-none font-display font-bold text-4xl md:text-5xl tracking-tight placeholder-white/20 text-white"
            data-testid={inputTestId}
          />
          <div className="flex items-center gap-2 shrink-0 glass rounded-xl px-3 py-2">
            <div className="w-6 h-6 rounded-full bg-gradient-to-br from-palm-teal to-palm-mint flex items-center justify-center text-[10px] font-black text-palm-surface">
              {tokenGlyph}
            </div>
            <span className="font-semibold text-sm">{unit}</span>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between text-xs">
          <span className="text-white/50">
            Balance:{' '}
            <span className="text-white/90 font-medium" data-testid={balanceTestId}>
              {fmtFull(maxAvailable)}
            </span>{' '}
            {unit}
          </span>
          <div className="flex items-center gap-1.5">
            {[25, 50, 75].map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => handleQuick(p)}
                className="px-2 py-1 rounded-md text-[11px] text-white/70 hover:text-palm-lime hover:bg-palm-lime/10 transition-all"
                data-testid={`quick-${p}-button`}
              >
                {p}%
              </button>
            ))}
            <button
              type="button"
              onClick={handleMax}
              className="px-2 py-1 rounded-md text-[11px] font-bold text-palm-lime hover:bg-palm-lime/10 transition-all"
              data-testid="max-button"
            >
              MAX
            </button>
          </div>
        </div>
        <div className="mt-3 h-1 rounded-full bg-white/5 overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-palm-teal to-palm-lime transition-all"
            style={{ width: `${percentOfMax}%` }}
          />
        </div>
      </div>
    </>
  );
}
